
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export interface BulkUnassignResult {
  success: boolean;
  successCount: number;
  failureCount: number;
  errors: string[];
}

/**
 * Bulk unassign courses from multiple users
 */
export const bulkUnassignCoursesFromUsers = async (
  assignmentIds: string[]
): Promise<BulkUnassignResult> => {
  const result: BulkUnassignResult = {
    success: false,
    successCount: 0,
    failureCount: 0,
    errors: []
  };

  if (!assignmentIds || assignmentIds.length === 0) {
    result.errors.push('No assignments selected');
    return result;
  }

  try {
    console.log(`🗑️ Bulk unassigning ${assignmentIds.length} course assignments`);

    // Delete course assignments in batch
    const { data, error } = await supabase
      .from('course_assignments')
      .delete()
      .in('id', assignmentIds)
      .select();

    if (error) {
      console.error('Error bulk unassigning courses:', error);
      result.errors.push(error.message);
      result.failureCount = assignmentIds.length;
      toast.error(`Failed to unassign courses: ${error.message}`);
      return result;
    }

    result.successCount = data?.length || 0;
    result.success = result.successCount > 0;

    if (result.success) {
      console.log(`✅ Successfully unassigned ${result.successCount} courses`);
      toast.success(`Successfully unassigned ${result.successCount} course${result.successCount > 1 ? 's' : ''}`);
    }

    return result;
  } catch (error: any) {
    console.error('Error in bulkUnassignCoursesFromUsers:', error);
    result.errors.push(error.message);
    result.failureCount = assignmentIds.length;
    toast.error('Network error while unassigning courses');
    return result;
  }
};

/**
 * Get all course assignments for bulk operations
 */
export const getAllCourseAssignments = async () => {
  try {
    const { data, error } = await supabase
      .from('course_assignments')
      .select('id, user_id, course_id, status, final_grade, assigned_date')
      .eq('assignment_status', 'assigned')
      .order('assigned_date', { ascending: false });

    if (error) throw error;

    // Get profiles separately to avoid relation issues
    const enriched = await Promise.all((data || []).map(async (assignment) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, email')
        .eq('id', assignment.user_id)
        .single();
      
      return { ...assignment, profiles: profile || { name: 'Unknown', email: '' } };
    }));

    return enriched;
  } catch (error) {
    console.error('Error fetching course assignments:', error);
    return [];
  }
};
